import type { AxiosRequestConfig } from 'axios';

import type { CreatePermissionSnapshotInput, SnapshotComparisonDto } from '@/libs/api-user-mgt/generated/model';
import { userManagementAxiosInstance } from '@/libs/api-user-mgt';
import {
  getApiUserManagementV1PermissionSnapshots,
  getApiUserManagementV1PermissionSnapshotsId,
  getApiUserManagementV1PermissionSnapshotsIdComparison,
  postApiUserManagementV1PermissionSnapshots
} from '@/libs/api-user-mgt/generated/permission-snapshots/permission-snapshots';

import { countSnapshotLiveChangedFeatures } from '@/userManagement/application/mappers/snapshotMapper';
import { mapPermissionDtoToEntity } from '@/userManagement/application/mappers/permissionMapper';
import type {
  PermissionSnapshotDetailWire,
  SnapshotCompareUserRowWire,
  SnapshotCompareWire,
  SnapshotPermissionWire
} from '@/userManagement/application/types/SnapshotWire';
import type { Permission } from '@/userManagement/domain/entities/Permission';
import type { PermissionSnapshot } from '@/userManagement/domain/entities/PermissionSnapshot';

import { featureCatalogMap } from '@/userManagement/application/mappers/featureCatalogMapper';
import { parseSieveListPayload } from './pagedWire';
import { fetchUserDirectoryBundle } from './directoryBundleFetch';
import {
  coerceAxiosSnapshotFileDataToArrayBuffer,
  normalizeSnapshotDetailWire,
  snapshotFileBytesToUtf8JsonText,
  snapshotFileJsonToDetailWire
} from './snapshotPermissionFile';
import { buildCompareUserRowsFromComparisonDto, unwrapSnapshotComparisonResponseBody } from './snapshotComparisonWire';
import { fetchPendingPermissionApprovalCountByUsernameLower } from './approvalsSegment';
import { fetchRoleTemplatesForRoleIds, fetchUserPermissions, finalizeUserPermissionsFromCached } from './userPermissionsFetch';
import { resolveSavedAtIso, resolveSavedByDisplay } from './snapshotMetadataWire';

const SNAPSHOT_GROUP_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SNAPSHOT_FILE_REQUEST: AxiosRequestConfig = { responseType: 'arraybuffer' };

async function enrichCompareUserRowsWithPendingPermissionApprovals(
  rows: SnapshotCompareUserRowWire[]
): Promise<SnapshotCompareUserRowWire[]> {
  if (rows.length === 0) return rows;
  const anyMissing = rows.some((r) => r.inProgressApprovalChangeCount === undefined);
  if (!anyMissing) return rows;
  try {
    const byUsername = await fetchPendingPermissionApprovalCountByUsernameLower();
    return rows.map((r) => {
      if (r.inProgressApprovalChangeCount !== undefined) return r;
      const n = byUsername.get(r.username.trim().toLowerCase()) ?? 0;
      if (n <= 0) return r;
      return { ...r, inProgressApprovalChangeCount: n };
    });
  } catch {
    return rows;
  }
}

async function fetchPermissionSnapshotFileBuffer(id: number): Promise<ArrayBuffer> {
  const fil = await userManagementAxiosInstance<ArrayBuffer>({
    url: `/api/user-management/v1/permission-snapshots/${id}/file`,
    method: 'GET',
    ...SNAPSHOT_FILE_REQUEST
  });
  return coerceAxiosSnapshotFileDataToArrayBuffer(fil.data);
}

function parsePositiveInt(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.floor(value);
  if (typeof value === 'string' && value.trim() !== '') {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
}

function isSnapshotGroupUuid(value: string): boolean {
  return SNAPSHOT_GROUP_UUID_RE.test(value.trim());
}

function parseSnapshotGroupIdFromRow(o: Record<string, unknown>): string {
  const candidates = [o.snapshotGroupId, o.SnapshotGroupId, o.snapshotId, o.SnapshotId];
  for (const c of candidates) {
    if (typeof c === 'string' && isSnapshotGroupUuid(c)) return c.trim();
  }
  return '';
}

/** Maps list/detail payload shapes (PascalCase or camelCase) to domain snapshot metadata. */
export function coercePermissionSnapshotFromUnknown(row: unknown): PermissionSnapshot | null {
  if (row === null || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const snapshotGroupId = parseSnapshotGroupIdFromRow(o);
  const idRaw = o.id ?? o.Id;
  let id = parsePositiveInt(idRaw);
  if (id <= 0 && snapshotGroupId === '') {
    const fallback = parsePositiveInt(o.snapshotId ?? o.SnapshotId);
    if (fallback > 0) id = fallback;
  }
  if (id <= 0 && snapshotGroupId === '') return null;
  const label = String(o.label ?? o.Label ?? '').trim();
  const asOfDate = String(o.asOfDate ?? o.AsOfDate ?? '').trim();
  if (!label || !asOfDate) return null;
  const userCount = parsePositiveInt(o.userCount ?? o.UserCount);
  const savedBy = resolveSavedByDisplay(o);
  const savedAt = resolveSavedAtIso(o);
  const out: PermissionSnapshot = {
    id,
    label,
    asOfDate,
    userCount,
    savedBy,
    savedAt
  };
  if (snapshotGroupId !== '') {
    out.snapshotGroupId = snapshotGroupId;
  }
  return out;
}

function emptySnapshotDetailWire(): PermissionSnapshotDetailWire {
  return { id: 0, label: '', asOfDate: '', userCount: 0, savedBy: 'Unknown', savedAt: '', permissions: [] };
}

/**
 * Loads snapshot detail: metadata from `GET .../{id}`, permissions from `GET .../{id}/file` (gzip JSON or raw JSON).
 */
async function loadSnapshotDetailByNumericId(id: number): Promise<PermissionSnapshotDetailWire> {
  let meta = emptySnapshotDetailWire();
  try {
    const metaR = await getApiUserManagementV1PermissionSnapshotsId(id);
    meta = normalizeSnapshotDetailWire(metaR.data);
  } catch {
    meta = { ...emptySnapshotDetailWire(), id };
  }

  try {
    const buf = await fetchPermissionSnapshotFileBuffer(id);
    if (buf.byteLength === 0) {
      return meta.id > 0 || meta.permissions.length > 0 ? { ...meta, id: meta.id || id } : { ...emptySnapshotDetailWire(), id };
    }
    const text = await snapshotFileBytesToUtf8JsonText(buf);
    const parsed = JSON.parse(text) as unknown;
    const fileWire = snapshotFileJsonToDetailWire(parsed, id);
    const hasPerUserIndex =
      (fileWire.snapshotPermissionsByCrossUserId != null && Object.keys(fileWire.snapshotPermissionsByCrossUserId).length > 0) ||
      (fileWire.snapshotPermissionsByUsernameLower != null && Object.keys(fileWire.snapshotPermissionsByUsernameLower).length > 0);
    if (fileWire.permissions.length > 0 || hasPerUserIndex || (fileWire.label !== '' && fileWire.label !== meta.label)) {
      return {
        ...meta,
        ...fileWire,
        id: fileWire.id > 0 ? fileWire.id : meta.id > 0 ? meta.id : id,
        permissions: fileWire.permissions.length > 0 ? fileWire.permissions : meta.permissions
      };
    }
  } catch {
    // File endpoint optional (e.g. older snapshots); metadata GET may still carry permissions.
  }

  return meta.id > 0 || meta.permissions.length > 0 ? { ...meta, id: meta.id || id } : { ...emptySnapshotDetailWire(), id };
}

/** `snapshotKey` must be numeric row id (see {@link snapshotListRowKey}); UUID-only keys are not loaded (OpenAPI has no group route). */
async function loadSnapshotDetailForKey(snapshotKey: string): Promise<PermissionSnapshotDetailWire> {
  const trimmed = snapshotKey.trim();
  if (trimmed === '') return emptySnapshotDetailWire();
  if (isSnapshotGroupUuid(trimmed)) {
    return emptySnapshotDetailWire();
  }
  const n = parseInt(trimmed, 10);
  if (!Number.isFinite(n) || n <= 0) return emptySnapshotDetailWire();
  return loadSnapshotDetailByNumericId(n);
}

function permissionEntityToSnapshotWire(p: Permission): SnapshotPermissionWire {
  return {
    featureId: p.featureId,
    featureName: p.featureName,
    category: p.category,
    allowRead: p.allowRead,
    allowWrite: p.allowWrite,
    allowDelete: p.allowDelete,
    allowCentralAccess: p.allowCentralAccess,
    allowExcelExport: p.allowExcelExport
  };
}

/** Resolves snapshot permission rows for one user (apply-document `entries` / `users`) or legacy flat `permissions`. */
function snapshotPermissionsForUser(detail: PermissionSnapshotDetailWire, crossUserId: string, username: string): SnapshotPermissionWire[] {
  const uid = crossUserId.trim();
  if (uid !== '') {
    const byId = detail.snapshotPermissionsByCrossUserId?.[uid];
    if (byId != null && byId.length > 0) {
      return byId;
    }
  }
  const key = username.trim().toLowerCase();
  if (key !== '') {
    const byName = detail.snapshotPermissionsByUsernameLower?.[key];
    if (byName != null && byName.length > 0) {
      return byName;
    }
  }
  return (Array.isArray(detail.permissions) ? detail.permissions : []) as SnapshotPermissionWire[];
}

export const userManagementSnapshotsSegment = {
  listSnapshots: async (): Promise<PermissionSnapshot[]> => {
    const r = await getApiUserManagementV1PermissionSnapshots();
    const { rows } = parseSieveListPayload<unknown>(r.data, r.headers, 0);
    const list = rows.length > 0 ? rows : Array.isArray(r.data) ? r.data : [];
    return list.map(coercePermissionSnapshotFromUnknown).filter((x): x is PermissionSnapshot => x !== null);
  },

  createSnapshot: async (data: { label: string; asOfDate: string }, idempotencyKey: string): Promise<PermissionSnapshot> => {
    const body: CreatePermissionSnapshotInput = {
      label: data.label,
      asOfDate: data.asOfDate,
      data: JSON.stringify({ label: data.label, asOfDate: data.asOfDate })
    };
    const r = await postApiUserManagementV1PermissionSnapshots(body, {
      headers: { 'X-Idempotency-Key': idempotencyKey }
    });
    const parsed = coercePermissionSnapshotFromUnknown(r.data);
    if (parsed) return parsed;
    return {
      id: 0,
      label: data.label,
      asOfDate: data.asOfDate,
      userCount: 0,
      savedBy: 'Unknown',
      savedAt: ''
    };
  },

  getSnapshotDetail: async (snapshotKey: string): Promise<PermissionSnapshotDetailWire> => loadSnapshotDetailForKey(snapshotKey),

  getSnapshotCompare: async (snapshotKey: string, crossUserId?: string, username?: string): Promise<SnapshotCompareWire> => {
    const detail = await loadSnapshotDetailForKey(snapshotKey);
    const uname = username?.trim() ?? '';
    const uid = crossUserId?.trim() ?? '';
    const snapshotPermissions = snapshotPermissionsForUser(detail, uid, uname);

    if (!crossUserId || uname === '') {
      return { snapshotPermissions, livePermissions: [] };
    }

    const live = await fetchUserPermissions(uname, crossUserId);
    const livePermissions = live.permissions.map(permissionEntityToSnapshotWire);
    return { snapshotPermissions, livePermissions };
  },

  getSnapshotCompareUsers: async (
    snapshotKey: string,
    query?: { q?: string; changesOnly?: boolean }
  ): Promise<SnapshotCompareUserRowWire[]> => {
    const trimmed = snapshotKey.trim();
    const snapshotNumericId = parseInt(trimmed, 10);
    if (!isSnapshotGroupUuid(trimmed) && Number.isFinite(snapshotNumericId) && snapshotNumericId > 0) {
      try {
        const res = await getApiUserManagementV1PermissionSnapshotsIdComparison(snapshotNumericId, {
          changedUsersPage: 1,
          changedUsersPageSize: 500
        });
        const dto = unwrapSnapshotComparisonResponseBody(res.data);
        if (dto != null) {
          return enrichCompareUserRowsWithPendingPermissionApprovals(buildCompareUserRowsFromComparisonDto(dto, query));
        }
      } catch {
        // Prefer server comparison when available; fall back to directory-bundle orchestration.
      }
    }

    const detail = await loadSnapshotDetailForKey(snapshotKey);

    let bundle;
    try {
      bundle = await fetchUserDirectoryBundle({
        listParams: { search: query?.q?.trim() || undefined }
      });
    } catch {
      return [];
    }

    const featureById = featureCatalogMap(bundle.features);
    const roleIds = bundle.entries.map((e) => e.permissionRoleId).filter((id): id is number => id != null && id > 0);
    let roleTemplates: Map<number, Permission[] | null>;
    try {
      roleTemplates = await fetchRoleTemplatesForRoleIds(roleIds, featureById);
    } catch {
      roleTemplates = new Map();
    }

    const rows: SnapshotCompareUserRowWire[] = bundle.entries.map((e) => {
      const { crossUserId: uid, username: uname, displayName, permissionRoleId, permissionsDto, user } = e;
      const snapshotPermsRaw = snapshotPermissionsForUser(detail, uid, uname);
      const snapshotPerms = snapshotPermsRaw.map((p) => mapPermissionDtoToEntity(p));
      try {
        const roleTemplate = permissionRoleId != null && permissionRoleId > 0 ? (roleTemplates.get(permissionRoleId) ?? null) : null;
        const live = finalizeUserPermissionsFromCached(
          {
            crossUserId: uid,
            permissionsDto,
            user,
            features: bundle.features,
            roleTemplate
          },
          undefined
        );
        const changeCount = countSnapshotLiveChangedFeatures(snapshotPerms, live.permissions);
        // Client fallback cannot detect Removed (directory is live-only). No snapshot grants → Added.
        const changeKind = snapshotPermsRaw.length === 0 ? 'Added' : 'Changed';
        return { userId: uid, username: uname, displayName, changeCount, changeKind };
      } catch {
        const changeKind = snapshotPermsRaw.length === 0 ? 'Added' : 'Changed';
        return { userId: uid, username: uname, displayName, changeCount: 0, changeKind };
      }
    });

    if (query?.changesOnly) {
      return enrichCompareUserRowsWithPendingPermissionApprovals(rows.filter((row) => row.changeKind === 'Added' || row.changeCount > 0));
    }
    return enrichCompareUserRowsWithPendingPermissionApprovals(rows);
  },

  getSnapshotComparison: async (
    snapshotKey: string,
    query?: { changedUsersPage?: number; changedUsersPageSize?: number }
  ): Promise<SnapshotComparisonDto | null> => {
    const trimmed = snapshotKey.trim();
    if (trimmed === '' || isSnapshotGroupUuid(trimmed)) return null;
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n) || n <= 0) return null;
    try {
      const res = await getApiUserManagementV1PermissionSnapshotsIdComparison(n, {
        changedUsersPage: query?.changedUsersPage ?? 1,
        changedUsersPageSize: query?.changedUsersPageSize ?? 50
      });
      return unwrapSnapshotComparisonResponseBody(res.data);
    } catch {
      return null;
    }
  }
};
